// Utility functions for action items table formatting and calculations
import { type ProjectActionItem } from '@consigli/types';

import { type ActionItemColumn, type GroupByOption, type SupportedItem } from './action-item-types';
import { isProjectActionItem } from './action-item-types';

/**
 * Converts snake_case status to Title Case display format
 * @example 'in_progress' -> 'In Progress'
 */
export const formatStatusToTitleCase = (status: string): string => {
  return status
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Formats a date string to European format (dd.MM.yyyy)
 * Uses UTC to avoid timezone inconsistencies.
 * Used internally as a stable sort key for deadline grouping.
 */
export const formatDateToEuropean = (dateString: string): string => {
  // Extract date portion directly from ISO string to avoid timezone conversion issues
  // dateString format: "2026-04-30T00:00:00" or "2026-04-30"
  const datePart = dateString.substring(0, 10); // "2026-04-30"
  const [year, month, day] = datePart.split('-');
  return `${day}.${month}.${year}`;
};

/**
 * Formats a date string using the given locale for display.
 * Extracts the date portion from the ISO string to avoid timezone shifts.
 */
export const formatDateToLocale = (dateString: string, locale: string): string => {
  const datePart = dateString.substring(0, 10);
  const [year, month, day] = datePart.split('-').map(Number);
  return new Date(year, month - 1, day).toLocaleDateString(locale, {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
};

/**
 * Parses a European formatted date string (d.M.yyyy) back to timestamp
 * @returns timestamp in milliseconds, or 0 for invalid/missing dates (sorts first)
 */
export const parseEuropeanDateToTimestamp = (formattedDate: string): number => {
  const parts = formattedDate.split('.');
  if (parts.length !== 3) return 0;
  const [day, month, year] = parts.map(Number);
  if (isNaN(day) || isNaN(month) || isNaN(year)) return 0;
  return new Date(year, month - 1, day).getTime();
};

/**
 * Returns Tailwind CSS classes for status badge styling
 */
export const getStatusBadgeClass = (status: string): string => {
  switch (status) {
    case 'completed':
      return 'bg-green-100 text-green-700';
    case 'overdue':
      return 'bg-red-100 text-red-700';
    case 'in_progress':
      return 'bg-blue-100 text-blue-700';
    default:
      return 'bg-gray-100 text-gray-600';
  }
};

/**
 * Column width constants for action items table
 * Centralized here for easy maintenance
 */
const COLUMN_WIDTHS = {
  title: 'flex-1 min-w-0',
  deadline: 'w-28 shrink-0 ml-4', // 7rem + left margin for spacing from title
  topic: 'w-40 shrink-0', // 10rem - wider for longer topic names
  status: 'w-24 shrink-0', // 6rem - fits "In Progress"
  person: 'w-40 shrink-0', // 10rem
  meeting: 'w-48 shrink-0', // 12rem - includes date subtitle
  default: 'flex-1 min-w-0',
} as const;

export const ACTIONS_COLUMN_WIDTH = 'w-36 shrink-0';

/**
 * Returns Tailwind classes for column sizing
 * Title takes remaining space (flex-1), other columns have consistent fixed widths
 */
export const getColumnWidth = (col: ActionItemColumn): string => {
  return COLUMN_WIDTHS[col] ?? COLUMN_WIDTHS.default;
};

/**
 * Extracts meeting datetime from the first item in a group
 * Only used at project-level when grouping by meeting
 */
export const getMeetingDatetimeFromGroup = (groupItems: ProjectActionItem[]): string | null => {
  if (groupItems.length === 0) return null;
  return groupItems[0].meetingScheduledStartTime ?? null;
};

/**
 * Checks if a name is a generic speaker placeholder (e.g., "Speaker 1", "Speaker 2")
 * These names are transcript-generated and don't represent actual identified people
 */
export const isGenericSpeakerName = (name: string | null | undefined): boolean => {
  if (!name) return false;
  return /^Speaker\s+\d+$/i.test(name.trim());
};

/**
 * Separator used to combine meeting context with speaker name in group keys
 */
const GROUP_KEY_SEPARATOR = ':::';

/**
 * Normalizes a meeting title to Title Case only if it's ALL CAPS.
 * Preserves original casing for titles that are already properly formatted.
 * Converts "STAND UP MEETING" to "Stand Up Meeting", but leaves "Daily Standup" unchanged.
 */
const normalizeMeetingTitle = (title: string | null | undefined): string | null => {
  if (!title) return null;

  // Only normalize if title is all uppercase
  if (title !== title.toUpperCase()) {
    return title;
  }

  return title
    .toLowerCase()
    .split(' ')
    .map((word) => (word.length > 0 ? word.charAt(0).toUpperCase() + word.slice(1) : ''))
    .join(' ');
};

/**
 * Generates grouping key, display name, and subtitle for a person.
 * For generic speaker names (e.g., "Speaker 1"), includes meeting context to avoid
 * incorrectly grouping speakers from different meetings together.
 *
 * @param name - The assigned person name
 * @param meetingId - The meeting ID for context (used as unique group key)
 * @param meetingTitle - The meeting title for display context
 * @param fallbackName - Fallback for unassigned items
 * @param meetingDate - The meeting scheduled start time for display context
 * @param locale - Locale string for date formatting
 * @param speakerPrefix - Translated word for "Speaker" (e.g. "Sprecher" in German)
 * @returns Object with groupKey (for internal grouping), displayName (for UI), and subtitle (meeting context)
 */
export const getPersonGroupKeyAndDisplay = (
  name: string | null | undefined,
  meetingId: string | null | undefined,
  meetingTitle: string | null | undefined,
  fallbackName: string,
  meetingDate?: string | null,
  locale?: string,
  speakerPrefix?: string,
): { groupKey: string; displayName: string; subtitle: string | null } => {
  if (!name) {
    return { groupKey: fallbackName, displayName: fallbackName, subtitle: null };
  }

  if (isGenericSpeakerName(name)) {
    // For generic speakers, scope the grouping to the meeting using meetingId (unique)
    const groupKey = `${meetingId || 'unknown'}${GROUP_KEY_SEPARATOR}${name}`;

    // Build subtitle with meeting title and date
    const dateShort = meetingDate
      ? locale
        ? formatDateToLocale(meetingDate, locale)
        : formatDateToEuropean(meetingDate)
      : null;
    const normalizedTitle = normalizeMeetingTitle(meetingTitle);
    let subtitle: string | null = null;
    if (normalizedTitle && dateShort) {
      subtitle = `${normalizedTitle}, ${dateShort}`;
    } else if (normalizedTitle) {
      subtitle = normalizedTitle;
    } else if (dateShort) {
      subtitle = dateShort;
    }

    // Build localized display name: extract number from "Speaker N" and prepend translated prefix
    const number = name.match(/\d+/)?.[0];
    const displayName = speakerPrefix && number ? `${speakerPrefix} ${number}` : name;

    return { groupKey, displayName, subtitle };
  }

  // For real names, group across all meetings (no subtitle needed)
  return { groupKey: name, displayName: name, subtitle: null };
};

/**
 * Get group key, display name, and subtitle for an item based on grouping option.
 * Pure function - no React dependencies.
 *
 * @param item - The action item to get grouping info for
 * @param groupBy - The current grouping option
 * @param translations - Object containing translated strings for fallback values
 * @returns Object with groupKey, displayName, and subtitle
 */
export const getGroupKeyAndDisplay = (
  item: SupportedItem,
  groupBy: GroupByOption,
  translations: {
    unassigned: string;
    noTopic: string;
    noDeadline: string;
    noMeeting: string;
    statusLabel?: (status: string) => string;
    speakerPrefix?: string;
  },
  locale?: string,
): { groupKey: string; displayName: string; subtitle: string | null } => {
  // Extract project-level fields safely (only available on ProjectActionItem)
  const meetingId = isProjectActionItem(item) ? item.meetingId : undefined;
  const meetingTitle = isProjectActionItem(item) ? item.meetingTitle : undefined;
  const meetingScheduledStartTime = isProjectActionItem(item)
    ? item.meetingScheduledStartTime
    : undefined;
  const topic = isProjectActionItem(item) ? item.topic : undefined;
  const dueDate = isProjectActionItem(item) ? item.dueDate : undefined;

  switch (groupBy) {
    case 'person': {
      // Use meeting-scoped grouping for generic speaker names (e.g., "Speaker 1")
      // to avoid incorrectly grouping speakers from different meetings together
      return getPersonGroupKeyAndDisplay(
        item.responsiblePerson?.name,
        meetingId,
        meetingTitle,
        translations.unassigned,
        meetingScheduledStartTime,
        locale,
        translations.speakerPrefix,
      );
    }
    case 'topic': {
      const key = topic ?? translations.noTopic;
      return { groupKey: key, displayName: key, subtitle: null };
    }
    case 'deadline': {
      const groupKey = dueDate ? formatDateToEuropean(dueDate) : translations.noDeadline;
      const displayName = dueDate
        ? locale
          ? formatDateToLocale(dueDate, locale)
          : formatDateToEuropean(dueDate)
        : translations.noDeadline;
      return { groupKey, displayName, subtitle: null };
    }
    case 'status': {
      const groupKey = item.completionStatus;
      const displayName = translations.statusLabel
        ? translations.statusLabel(item.completionStatus)
        : formatStatusToTitleCase(item.completionStatus);
      return { groupKey, displayName, subtitle: null };
    }
    case 'meeting': {
      // Use meetingId as key for uniqueness (meetings can have same title on different days)
      // Display shows title - the date is shown separately in the group header
      const groupKey = meetingId ?? translations.noMeeting;
      const display = meetingTitle ?? translations.noMeeting;
      return { groupKey, displayName: display, subtitle: null };
    }
    default: {
      const key = translations.unassigned;
      return { groupKey: key, displayName: key, subtitle: null };
    }
  }
};
